// @ts-check

/**
 * @file Renders user detail navigation links.
 */

import {Link} from "expo-router"
import {memo} from "react"
import {ShapeComponent, shapeComponent} from "set-state-compare/build/shape-component.js"
import {View} from "react-native-propforge"
import {routePathWithParams, TRANSLATIONS} from "./shared"

/** @import {NamedExoticComponent, ReactNode} from "react" */
/** @type {Record<string, object>} */
const styles = {}

/**
 * @typedef {object} UserNavBarProps
 * @property {string} active Active navigation tab key.
 * @property {"en" | "da" | "de"} locale Locale used for navigation labels.
 * @property {Record<string, string | string[] | undefined>} params Current route params.
 * @property {string | number} userId User identifier for detail links.
 */

/** @typedef {Record<string, never>} UserNavBarState */

/**
 * User detail navigation bar.
 * @augments {ShapeComponent<UserNavBarProps, UserNavBarState>}
 */
class UserNavBar extends ShapeComponent {
  /**
   * Renders navigation links for the selected user.
   * @returns {ReactNode} User detail navigation content.
   */
  render() {
    const {active, locale, params, userId} = this.p
    const translation = TRANSLATIONS[locale]
    return (
      <View
        style={styles.container ||= {
          flexDirection: "row",
          gap: 12,
          marginBottom: 16
        }}
        testID="userRolesNavBar"
      >
        <Link
          href={routePathWithParams(params, `/users/${userId}`)}
          style={styles[`generalLink-${active === "general"}`] ||= {
            backgroundColor: active === "general" ? "#111827" : "#e5e7eb",
            borderRadius: 999,
            color: active === "general" ? "#ffffff" : "#111827",
            fontSize: 14,
            fontWeight: 600,
            paddingHorizontal: 16,
            paddingVertical: 10
          }}
          testID="userRolesNavBar/generalLink"
        >
          {translation.general}
        </Link>
        <Link
          href={routePathWithParams(params, `/users/${userId}/user-roles`)}
          style={styles[`userRolesLink-${active === "userRoles"}`] ||= {
            backgroundColor: active === "userRoles" ? "#111827" : "#e5e7eb",
            borderRadius: 999,
            color: active === "userRoles" ? "#ffffff" : "#111827",
            fontSize: 14,
            fontWeight: 600,
            paddingHorizontal: 16,
            paddingVertical: 10
          }}
          testID="userRolesNavBar/userRolesLink"
        >
          {translation.userRoles}
        </Link>
      </View>
    )
  }
}

/** @type {NamedExoticComponent<UserNavBarProps>} */
const UserNavBarComponent = memo(shapeComponent(UserNavBar))

export default UserNavBarComponent
